import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  buildCareWalkthrough,
  summarizeWalkthrough,
  walkthroughMomentIndexes,
} from '@shared/consumerPlan';
import { usePlanTotals } from './use-plan-totals';

const MOMENT_PARAM = 'moment';

/**
 * The selected policy's claim projection replayed as care bills, with the key moments the
 * consumer steps through. The current moment lives in the URL so Back steps back a moment.
 */
export function useCareWalkthrough(clientId: string) {
  const totals = usePlanTotals(clientId);
  const projection = totals.hasInsurance ? (totals.selectedProjection?.projection ?? null) : null;
  const [searchParams, setSearchParams] = useSearchParams();

  const months = useMemo(
    () => (projection ? buildCareWalkthrough(projection) : []),
    [projection],
  );
  const moments = useMemo(() => (projection ? walkthroughMomentIndexes(projection) : []), [projection]);
  const summary = useMemo(
    () => (projection ? summarizeWalkthrough(months, projection) : null),
    [months, projection],
  );

  const position = Math.min(Number(searchParams.get(MOMENT_PARAM) ?? 0) || 0, Math.max(0, moments.length - 1));
  const isLastMoment = position >= moments.length - 1;

  const showNextMoment = () => setSearchParams({ [MOMENT_PARAM]: String(position + 1) });
  const showPreviousMoment = () => setSearchParams({ [MOMENT_PARAM]: String(position - 1) });

  return {
    ...totals,
    canWalkThrough: months.length > 0,
    moment: months[moments[position]] ?? null,
    position,
    momentCount: moments.length,
    isLastMoment,
    showNextMoment,
    showPreviousMoment,
    summary,
  };
}
